import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { createProjectServer, getProjectByIdServer } from '@/lib/server/project.server';

type ProjectImage = {
  id: string;
  project_id: string;
  image_url: string;
  created_at: string;
  updated_at: string | null;
};

type Project = {
  id: string;
  title: string;
  description: string;
  size: string | null;
  body_placement: string | null;
  custom_placement: string | null;
  inspiration_text: string | null;
  inspiration_urls: string | null;
  images: Array<ProjectImage>;
  status: {
    id: string;
    name: string;
    slug: string;
    color: string;
    display_order: number;
    created_at: string;
    updated_at: string;
  };
  created_at: string;
  client: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    phone: string | null;
  };
};

export const projectQueries = {
  byId: (id: string) =>
    queryOptions({
      queryKey: ['project', id],
      queryFn: () => getProjectByIdServer({ data: id }) as Promise<Project>,
      staleTime: 5 * 60 * 1000, // 5 minutes
    }),
};

export function useGetProjectById(id: string) {
  return useQuery(projectQueries.byId(id));
}

type CreateProjectPayload = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  description: string;
  size: string;
  bodyPlacement: string;
  customPlacement: string;
  inspirationText: string;
  inspirationUrls: string;
  inspirationImages: Array<string>; // Base64 data URLs
};

export function useCreateProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateProjectPayload) => createProjectServer({ data: payload }),
    onSuccess: () => {
      toast.success('Votre demande a été envoyée avec succès !');

      // Invalidate projects cache
      queryClient.invalidateQueries({ queryKey: ['project'] });
    },
    onError: (error: Error) => {
      console.error('Error creating project:', error);
      toast.error(error.message || "Une erreur s'est produite lors de l'envoi de votre demande.");
    },
  });
}
